import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { useWorkspaceService } from '@web/app/features/home/hooks';
import { getWorkspaceBadge } from '@web/app/shared';
import { useSelectedStore } from '@web/app/shared/stores';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { Button, Skeleton } from '@codes/ui-kit';

interface WorkspaceSwitcherProps {
    onOpenCreateWorkspace: () => void;
}

export const WorkspaceSwitcher = ({ onOpenCreateWorkspace }: WorkspaceSwitcherProps) => {
    const { clearSelection } = useSelectedStore();
    const { myWorkspacesQueryResult, workspaceFromServerQueryData, replaceWorkspace, workspaceFromServer } =
        useWorkspaceService();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const isInitialLoading = workspaceFromServerQueryData.isFetching && !workspaceFromServer;

    if (isInitialLoading) {
        return <Skeleton className="h-6 w-36" />;
    }

    if (workspaceFromServerQueryData.error || !workspaceFromServer) {
        return (
            <div className="flex gap-x-2 items-center">
                <div className="text-error text-sm">{t('workspace.error.fetchDetail')}</div>
                <Button
                    className="text-sm p-2 h-auto"
                    onClick={() => workspaceFromServerQueryData?.refetch?.()}
                    disabled={workspaceFromServerQueryData?.isRefetching}
                >
                    {t('workspace.error.retry')}
                </Button>
            </div>
        );
    }

    const isWorkspaceListLoading = myWorkspacesQueryResult.isFetching || myWorkspacesQueryResult.isRefetching;

    return (
        <>
            <DropdownMenuTrigger asChild className="max-sm:max-w-[180px]">
                <button className="flex items-center gap-1.5 py-1 px-2 rounded-md hover:bg-hover transition-colors duration-150 focus:outline-none">
                    <span className="text-sm font-medium truncate">{workspaceFromServer.name}</span>
                    <div className="max-xs:hidden">{getWorkspaceBadge(workspaceFromServer.stereo)}</div>
                    <ChevronsUpDown className="shrink-0 w-3.5 h-3.5 text-text-500" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[240px] p-1.5 rounded-lg space-y-0.5">
                {isWorkspaceListLoading ? (
                    <div className="max-h-48 flex-col flex gap-y-1 overflow-y-auto">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Skeleton key={i} className="h-8" />
                        ))}
                    </div>
                ) : myWorkspacesQueryResult.isError ? (
                    <div className="flex flex-col gap-y-2 items-center">
                        <div className="text-error text-sm">{t('workspace.error.fetchList')}</div>
                        <Button
                            className="text-sm p-2 h-auto"
                            onClick={() => myWorkspacesQueryResult.refetch()}
                            disabled={myWorkspacesQueryResult?.isRefetching}
                        >
                            {t('workspace.error.retry')}
                        </Button>
                    </div>
                ) : (
                    <div className="max-h-48 overflow-y-auto">
                        {myWorkspacesQueryResult.data?.data?.map(workspace => (
                            <DropdownMenuItem
                                key={workspace.id}
                                onClick={() => replaceWorkspace(workspace.id as string, workspace.siteId as string)}
                                className="cursor-pointer !rounded-md py-1.5 px-2 text-xs gap-1.5 hover:bg-hover truncate"
                            >
                                <div className="w-4 h-4 flex items-center justify-center">
                                    {workspace.id === workspaceFromServer.id && (
                                        <Check className="w-4 h-4 text-text-800" />
                                    )}
                                </div>
                                <div className="flex-1 flex gap-x-2 items-center justify-between min-w-0">
                                    <span className="truncate flex-1">{workspace.name}</span>
                                    {getWorkspaceBadge(workspace.stereo, false, 'h-[18px] px-2 text-xs')}
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </div>
                )}
                <DropdownMenuSeparator className="m-0" />
                <DropdownMenuItem
                    className="cursor-pointer !rounded-md py-1.5 px-2 text-xs text-text-500 hover:bg-hover"
                    onClick={() => {
                        clearSelection();
                        navigate('/w');
                    }}
                >
                    {t('workspace.allWorkspace')}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="m-0" />
                <DropdownMenuItem
                    onClick={() => onOpenCreateWorkspace()}
                    className="cursor-pointer !rounded-md py-1.5 px-2 text-xs font-normal text-text-500 hover:bg-hover"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    <span>{t('workspace.list.newWorkspace')}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </>
    );
};
